import {
  ActionLink,
  Avatar,
  Box,
  Columns,
  Container,
  Divider,
  Heading,
  SocialLinks,
  Stack,
  Text,
  UnstyledLink,
  Visible,
} from '@spaceship-fspl/components';
import { formatDate } from '@spaceship-fspl/helpers';
import { borderColor, display, text } from '@spaceship-fspl/styles';
import { PostOrPage, PostsOrPages } from '@tryghost/content-api';
import {
  Article,
  ArticleCard,
  ArticleContentContainer,
  ArticleFeatureImage,
  BlogTitle,
  BlogVerticalMarginContainer,
  getBlogPageLayoutStaticProps,
  getMetadataPropsFromGhostPostPageTagOrAuthor,
  Metadata,
  MetadataProps,
  NewsletterPanel,
  ScrollableArticleCards,
  StickyArticleHeader,
  withBlogArticlePageLayout,
} from 'components';
import {
  getAvatarImageSrcset,
  rewriteGhostImagesInHTML,
  rewriteGhostImageURL,
} from 'data/ghost';
import { getPost, getPosts } from 'data/ghost/api-cached';
import { GetStaticPaths, NextPage } from 'next';
import Link from 'next/link';
import { ParsedUrlQuery } from 'querystring';
import React from 'react';
import styled from 'styled-components';

const CategoryLink = styled(ActionLink)`
  ${text({ variant: 2 })}
  font-weight: 500;
`;

const AuthorAvatar = styled(Avatar)``;

const AuthorLink = styled(UnstyledLink)`
  :hover ${AuthorAvatar}, :focus ${AuthorAvatar} {
    ${borderColor('indigo.060')}
  }
`;

const ArticleTitleSection: React.FC<
  React.PropsWithChildren<{ post: PostOrPage }>
> = ({ post }) => (
  <Columns alignX="center">
    <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 8 / 12 }}>
      <Stack spaceY={{ xs: 'lg', md: 'md' }}>
        <Stack spaceY="sm">
          {post.primary_tag && (
            <Text variant={2} align={{ xs: 'center', md: 'left' }}>
              <Link
                href="/learn/tag/[slug]/"
                as={`/learn/tag/${post.primary_tag.slug}/`}
                passHref={true}
                legacyBehavior
              >
                <CategoryLink color="indigo.070">
                  {post.primary_tag?.name}
                </CategoryLink>
              </Link>
            </Text>
          )}
          <Stack spaceY="md">
            <BlogTitle align={{ xs: 'center', md: 'left' }}>
              {post.title}
            </BlogTitle>
            <Text
              variant={1}
              color="neutral.080"
              align={{ xs: 'center', md: 'left' }}
            >
              {post.excerpt}
            </Text>
          </Stack>
        </Stack>

        <Columns alignY="center" spaceX="sm" spaceY="sm">
          <Columns.Column width={{ xs: 1, md: 1 / 2 }}>
            <Link
              href="/learn/author/[slug]/"
              as={`/learn/author/${post.primary_author?.slug}/`}
              legacyBehavior
            >
              <AuthorLink>
                <Box display="flex" alignItems="center">
                  <Visible component="div" isHidden={{ xs: true, md: false }}>
                    <AuthorAvatar
                      size="md"
                      aria-describedby="article-author-name"
                      src={
                        post.primary_author?.profile_image
                          ? rewriteGhostImageURL(
                              post.primary_author?.profile_image,
                            )
                          : undefined
                      }
                      srcset={
                        post.primary_author?.profile_image
                          ? getAvatarImageSrcset(
                              post.primary_author?.profile_image,
                            )
                          : undefined
                      }
                      alt={`${post.primary_author?.name} profile image`}
                    />
                  </Visible>

                  <Box flex={1} marginLeft="xs" minWidth="0">
                    <Text
                      id="article-author-name"
                      variant={3}
                      color="neutral.100"
                      align={{ xs: 'center', md: 'left' }}
                      isBold={true}
                      isTruncated={true}
                    >
                      {`By ${post.primary_author?.name}` || ''}
                    </Text>
                  </Box>
                </Box>
              </AuthorLink>
            </Link>
          </Columns.Column>

          <Columns.Column width={{ xs: 1, md: 1 / 2 }}>
            <Text
              variant={3}
              color="neutral.080"
              align={{ xs: 'center', md: 'right' }}
              whiteSpace="nowrap"
            >
              {post.published_at &&
                `${formatDate(new Date(post.published_at), 'dd MMMM yyyy')} · `}
              {post.reading_time} min read
            </Text>
          </Columns.Column>
        </Columns>
      </Stack>
    </Columns.Column>
  </Columns>
);

interface AuthorSectionProps {
  name: string;
  profileImage: string;
  bio: string;
  slug: string;
}

const AuthorSection: React.FC<React.PropsWithChildren<AuthorSectionProps>> = ({
  name,
  profileImage,
  bio,
  slug,
}) => (
  <Stack spaceY={{ xs: 'md', md: 'lg' }}>
    <Divider color="neutral.050" />

    <Columns alignX="center" alignY="center">
      <Columns.Column width={{ sm: 1, md: 3 / 6, lg: 3 / 8 }}>
        <Link href="/learn/author/[slug]/" as={`/learn/author/${slug}/`}>
          <AuthorLink>
            <Box
              marginBottom={{ xs: bio ? 'sm' : 'none', md: 'none' }}
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              display="flex"
              alignItems="center"
            >
              <AuthorAvatar
                aria-describedby="article-author-full-name"
                size="lg"
                src={profileImage}
                srcset={getAvatarImageSrcset(profileImage)}
                alt={`${name} profile image`}
              />
              <Box marginLeft="sm">
                <Text variant={4} color="neutral.080">
                  WORDS BY
                </Text>
                <Text id="article-author-full-name" variant={1} isBold={true}>
                  {name}
                </Text>
              </Box>
            </Box>
          </AuthorLink>
        </Link>
      </Columns.Column>

      {bio && (
        <Columns.Column width={{ xs: 1, md: 3 / 6, lg: 5 / 8 }}>
          <Text
            variant={3}
            color="neutral.085"
            align={{ xs: 'center', md: 'left' }}
          >
            {bio}
          </Text>
        </Columns.Column>
      )}
    </Columns>

    <Divider color="neutral.050" />
  </Stack>
);

const RelatedPostSection: React.FC<
  React.PropsWithChildren<{ related: PostsOrPages }>
> = ({ related }) => (
  <Container>
    <Box marginBottom={{ xs: 'md', md: 'lg', lg: 'md' }}>
      <Heading variant={3} align="center">
        Related articles
      </Heading>
    </Box>
    <ScrollableArticleCards>
      {related.map(
        ({
          title,
          slug,
          feature_image,
          excerpt,
          primary_author,
          reading_time,
          primary_tag,
        }) => (
          <ArticleCard
            key={slug}
            slug={slug}
            title={title}
            image={feature_image}
            tag={primary_tag}
            description={excerpt}
            author={{
              ...primary_author,
              profileImage: primary_author?.profile_image,
            }}
            readingTime={reading_time}
          />
        ),
      )}
    </ScrollableArticleCards>
  </Container>
);

const StickySocialLinks = styled.div`
  ${display({ xs: 'none', md: 'block' })}
  position: sticky;
  top: 136px;
`;

interface PostPageProps {
  post: PostOrPage;
  related: PostsOrPages;
  metadata?: MetadataProps;
}

const PostPage: NextPage<PostPageProps> = ({ post, related, metadata }) => {
  const { primary_author } = post;

  const author: AuthorSectionProps = {
    name: primary_author?.name || '',
    profileImage: primary_author?.profile_image || '',
    bio: primary_author?.bio || '',
    slug: post.primary_author?.slug || '',
  };

  const target = React.useRef(null);

  return (
    <>
      <BlogVerticalMarginContainer>
        <StickyArticleHeader progressBarTarget={target}>
          {post.title}
        </StickyArticleHeader>
        <Container>
          {metadata && <Metadata {...metadata} />}
          <Stack spaceY={{ xs: 'lg', md: 'none' }}>
            <ArticleTitleSection post={post} />

            <Visible
              component="div"
              displayValue="block"
              isHidden={{ md: true }}
            >
              <SocialLinks
                variant="dark"
                size="lg"
                orientation="horizontal"
                space="between"
                shouldShowRSS
                sharePostUrl={post.url}
              />
            </Visible>
          </Stack>
        </Container>

        <ArticleFeatureImage
          url={
            post.feature_image
              ? rewriteGhostImageURL(post.feature_image)
              : undefined
          }
        />

        <Stack spaceY={{ xs: 'lg', md: 'xl' }}>
          <Container>
            <Columns>
              <Columns.Column width={{ xs: 1, md: 1 / 8, lg: 2 / 12 }}>
                <StickySocialLinks>
                  <SocialLinks
                    variant="dark"
                    size="lg"
                    orientation="vertical"
                    space="sm"
                    shouldShowRSS
                    sharePostUrl={post.url}
                  />
                </StickySocialLinks>
              </Columns.Column>

              <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 8 / 12 }}>
                <Article>
                  <ArticleContentContainer
                    ref={target}
                    dangerouslySetInnerHTML={{
                      __html: post.html
                        ? rewriteGhostImagesInHTML(post.html)
                        : '',
                    }}
                  />
                </Article>
              </Columns.Column>
            </Columns>
          </Container>

          <Stack spaceY={{ xs: 'xl', md: 'xxl', lg: 'xxxl' }}>
            <Container>
              <Columns alignX="center">
                <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 8 / 12 }}>
                  <Stack spaceY={{ xs: 'lg', md: 'xl' }}>
                    <Text variant={4} color="neutral.080" align="center">
                      The information in this article is prepared by Spaceship
                      Capital Limited (ABN 67 621 011 649, AFSL 501605). It is
                      general in nature as it has been prepared without taking
                      account of your objectives, financial situation or needs.
                    </Text>
                    <AuthorSection {...author} />
                  </Stack>
                </Columns.Column>
              </Columns>
            </Container>
            {related?.length > 0 && (
              <div>
                <RelatedPostSection related={related} />
              </div>
            )}
          </Stack>
        </Stack>
      </BlogVerticalMarginContainer>

      <NewsletterPanel />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = await getPosts({ limit: 'all' });

  return {
    paths: posts.map((post) => ({
      params: {
        slug: post.slug,
      },
    })),
    fallback: false,
  };
};

interface Context extends ParsedUrlQuery {
  slug: string;
}

export const getStaticProps = getBlogPageLayoutStaticProps<
  PostPageProps,
  Context
>(async (context) => {
  const slug = context?.params?.slug ?? '';
  const post = await getPost(slug);
  const related = await getPosts({
    tag: post.primary_tag?.slug,
    limit: 3,
    ignore: slug,
  });

  return {
    props: {
      post,
      related,
      metadata: getMetadataPropsFromGhostPostPageTagOrAuthor(post),
    },
  };
});

export default withBlogArticlePageLayout()(PostPage);
